import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Stack, 
  Card,
  IconButton,
  Divider,
  Badge,
  Grid,
  AspectRatio
} from '@mui/joy';
import { FaTrash, FaEdit, FaPlus, FaMinus } from 'react-icons/fa';
import { useWindowBasketStore, WindowBasketItem } from '../hooks/useWindowBasket';
import { windowTypes } from './WindowTypeSelector';
import { getWindowImageUrlSync, DEFAULT_IMAGE } from '../utils/windowImages';
import { WINDOW_STYLES, WindowStyleConfig } from '../config/windowStyles';

// Import window style related interfaces and functions from WindowDesigner
import { WindowStyle } from './WindowDesigner';
import { IconWrapper } from './IconWrapper';

interface WindowBasketProps {
  onClose?: () => void;
  onEditItem?: (item: WindowBasketItem) => void;
}

const WindowBasket: React.FC<WindowBasketProps> = ({ onClose, onEditItem }) => {
  const { 
    items, 
    removeItem, 
    updateQuantity, 
    getTotalPrice, 
    getItemCount,
    customerReference,
    clearItems
  } = useWindowBasketStore();

  const handleRemove = (id: string) => {
    // First check if this is the item being edited (if editingItem state exists in parent component)
    // If using onEditItem, we assume parent has the ability to handle edit cancellation
    if (onEditItem) {
      // Using a custom event to notify parent components that an item was removed
      // This allows the WindowDesigner component to clear its editing state
      const event = new CustomEvent('window-basket-item-removed', { 
        detail: { itemId: id }
      });
      window.dispatchEvent(event);
    }
    
    // Then remove the item from the basket
    removeItem(id);
  };

  const handleEdit = (item: WindowBasketItem) => {
    console.log("WindowBasket - Handling Edit:", item);
    console.log("Profile Type:", item.profileType); 
    console.log("Reference:", item.reference);
    
    if (onEditItem) {
      // Make sure we're passing the complete item with all properties
      const completeItem: WindowBasketItem = {
        ...item,
        // Ensure these critical fields are explicitly defined
        profileType: item.profileType,
        reference: item.reference || '',
      };
      
      onEditItem(completeItem);
    }
  };

  const incrementQuantity = (id: string, currentQuantity: number) => {
    updateQuantity(id, currentQuantity + 1);
  };

  const decrementQuantity = (id: string, currentQuantity: number) => {
    if (currentQuantity > 1) {
      updateQuantity(id, currentQuantity - 1);
    }
  };

  const getWindowTypeName = (typeId: string) => {
    return windowTypes.find(type => type.id === typeId)?.name || typeId;
  };

  // Function to find the image path for a window style
  const getWindowStyleImage = (windowStyleId: number, windowType: string, storedImagePath?: string): string => {
    if (storedImagePath) {
      return storedImagePath;
    }
    
    // Find the style in the global window styles configuration
    const style = WINDOW_STYLES.find(s => s.id === windowStyleId);
    
    if (style) {
      return style.previewPath || DEFAULT_IMAGE;
    }
    
    // Fallback to default image
    return DEFAULT_IMAGE;
  };

  // Format frame color to be more readable
  const formatFrameColor = (color: string): string => {
    if (color === 'white-smooth-both') return 'White Smooth (both sides)';
    if (color === 'black-noir-both') return 'Black Noir (both sides)';
    if (color === 'honey-oak-both') return 'Honey Oak (both sides)';
    return color.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography level="h3" component="h2">
          Window Quote
        </Typography>
        <Badge badgeContent={getItemCount()} color="primary">
          <Typography level="title-lg" sx={{ fontWeight: 'bold' }}>
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </Typography>
        </Badge>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {customerReference && (
          <Card
            variant="outlined" 
            sx={{ 
              mb: 3, 
              p: 1.5, 
              bgcolor: 'primary.50', 
              borderRadius: 'md',
              borderColor: 'primary.200'
            }}
          >
            <Typography level="body-md" fontWeight="medium">
              Customer Reference: <strong>{customerReference}</strong>
            </Typography>
          </Card>
        )}
        
        {items.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            p: 3
          }}>
            <Typography level="body-lg" sx={{ mb: 2, textAlign: 'center' }}>
              Your window quote is empty
            </Typography>
            <Button 
              variant="outlined" 
              onClick={onClose}
              startDecorator={<IconWrapper icon={FaPlus} />}
            >
              Design Windows
            </Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {items.map((item) => (
              <Card
                key={item.id}
                variant="outlined"
                sx={{ 
                  overflow: 'visible',
                  position: 'relative'
                }}
              >
                <Grid container spacing={2}>
                  <Grid xs={12} sm={4}>
                    <Box sx={{ 
                      width: '100%', 
                      height: 200,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: 'md',
                      backgroundColor: '#f9f9f9',
                      p: 2,
                      mb: { xs: 2, sm: 0 }
                    }}>
                      <Box 
                        component="img"
                        src={getWindowStyleImage(item.windowStyle, item.windowType, item.imagePath)}
                        alt={`${getWindowTypeName(item.windowType)} Style ${item.windowStyle}`}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain'
                        }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          const imgElement = e.currentTarget;
                          const container = imgElement.parentElement;
                          if (container) {
                            // Create a fallback div
                            const fallbackDiv = document.createElement('div');
                            fallbackDiv.style.display = 'flex';
                            fallbackDiv.style.alignItems = 'center';
                            fallbackDiv.style.justifyContent = 'center';
                            fallbackDiv.style.height = '100%';
                            fallbackDiv.style.color = '#777';
                            fallbackDiv.textContent = getWindowTypeName(item.windowType);
                            
                            // Clear the container and append the fallback
                            container.innerHTML = '';
                            container.appendChild(fallbackDiv);
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid xs={12} sm={8}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Typography level="title-md" sx={{ mb: 1 }}>
                        {getWindowTypeName(item.windowType)} - Style {item.windowStyle}
                      </Typography>
                      
                      {/* Display reference if available */}
                      {item.reference && (
                        <Typography level="body-sm" fontWeight="bold" color="primary">
                          Ref: {item.reference}
                        </Typography>
                      )}

                      <Box sx={{ mb: 1, mt: item.reference ? 1 : 0 }}>
                        <Typography level="body-sm">
                          <strong>Window Colour:</strong> {formatFrameColor(item.frameColor)}
                        </Typography>
                        
                        <Typography level="body-sm">
                          <strong>Glass:</strong> {item.glassType === 'clear' && item.clearGlassType 
                            ? `Clear (${item.clearGlassType.charAt(0).toUpperCase() + item.clearGlassType.slice(1)})` 
                            : item.glassType === 'frosted' && item.obscuredGlassType
                              ? `Obscured (${item.obscuredGlassType})`
                              : item.glassType.charAt(0).toUpperCase() + item.glassType.slice(1)}
                        </Typography>
                        
                        {/* Display window specifications if provided */}
                        {(item.width || item.height) && (
                          <Typography level="body-sm">
                            <strong>Dimensions:</strong> W: {item.width ? `${item.width}mm` : "N/A"}, H: {item.height ? `${item.height}mm` : "N/A"}
                          </Typography>
                        )}
                        
                        {item.profileDepth && (
                          <Typography level="body-sm">
                            <strong>Profile Depth:</strong> {item.profileDepth}mm
                          </Typography>
                        )}
                        
                        {item.profileType && (
                          <Typography level="body-sm" fontWeight="medium">
                            <strong>Profile Type:</strong> {item.profileType}
                          </Typography>
                        )}
                        
                        {item.beadingType && (
                          <Typography level="body-sm">
                            <strong>Beading:</strong> {item.beadingType}
                          </Typography>
                        )}
                        
                        {/* Improved cill information display */}
                        {item.cillIncluded && (
                          <Typography level="body-sm">
                            <strong>Cill:</strong> Included in height
                            {item.cillType && ` (${item.cillType})`}
                          </Typography>
                        )}
                        
                        {!item.cillIncluded && item.cillRequired && (
                          <Typography level="body-sm">
                            <strong>Cill:</strong> Required, not included in overall height
                            {item.cillType ? ` (${item.cillType}` : ''}
                            {item.cillHeight ? `, ${item.cillHeight}mm height)` : item.cillType ? ')' : ''}
                          </Typography>
                        )}
                        
                        {(!item.cillIncluded && !item.cillRequired) && (
                          <Typography level="body-sm">
                            <strong>Cill:</strong> Not required
                          </Typography>
                        )}
                        
                        {/* Display trickle vents status */}
                        <Typography level="body-sm">
                          <strong>Trickle Vents?:</strong> {item.trickleVents ? 'Yes' : 'No'}
                        </Typography>
                        
                        {item.notes && (
                          <Typography level="body-sm" sx={{ mt: 1, fontStyle: 'italic' }}>
                            <strong>Notes:</strong> {item.notes}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          {/* Price removed */}
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton 
                            variant="outlined" 
                            color="neutral" 
                            size="sm"
                            onClick={() => decrementQuantity(item.id, item.quantity)}
                            disabled={item.quantity <= 1}
                          >
                            <IconWrapper icon={FaMinus} />
                          </IconButton>
                          
                          <Typography sx={{ minWidth: '2rem', textAlign: 'center' }}>
                            {item.quantity}
                          </Typography>
                          
                          <IconButton 
                            variant="outlined" 
                            color="neutral" 
                            size="sm"
                            onClick={() => incrementQuantity(item.id, item.quantity)}
                          >
                            <IconWrapper icon={FaPlus} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                
                <Box sx={{ 
                  position: 'absolute', 
                  top: '0.5rem', 
                  right: '0.5rem', 
                  display: 'flex', 
                  gap: 1 
                }}>
                  {onEditItem && (
                    <IconButton 
                      size="sm" 
                      variant="plain" 
                      color="neutral" 
                      onClick={() => handleEdit(item)}
                    >
                      <IconWrapper icon={FaEdit} />
                    </IconButton>
                  )}
                  
                  <IconButton 
                    size="sm" 
                    variant="plain" 
                    color="danger" 
                    onClick={() => handleRemove(item.id)}
                  >
                    <IconWrapper icon={FaTrash} />
                  </IconButton>
                </Box>
              </Card>
            ))}
          </Stack>
        )}
      </Box>

      {items.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button 
              color="danger"
              variant="soft"
              onClick={clearItems}
              fullWidth
            >
              Empty Basket
            </Button>
            <Button 
              color="neutral" 
              variant="outlined" 
              onClick={onClose} 
              fullWidth
            >
              Continue Shopping
            </Button>
            <Button 
              color="success" 
              variant="solid" 
              fullWidth
              onClick={() => {
                if (onClose) {
                  onClose();
                }
                window.location.href = '/window-checkout';
              }}
              sx={{ minWidth: '120px' }}
            >
              Request Price
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default WindowBasket; 